'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

import { BannerDeviceGuides } from './banner-device-guides';
import { CropHandles } from './crop-handles';
import {
  clamp,
  createInitialSelection,
  loadHtmlImage,
  mapSelectionToRotatedPixels,
  resizeRectSelection,
} from './crop-geometry';
import type { CropHandle, ImageLayout, RectSelection, SelectionPixelsChangeHandler } from './types';

interface FreeformCropStageProps {
  imageSrc: string;
  rotation: number;
  mode: 'avatar' | 'banner';
  aspect: number;
  minWidth: number;
  rounded: boolean;
  onSelectionPixelsChange: SelectionPixelsChangeHandler;
}

export function FreeformCropStage({
  imageSrc,
  rotation,
  mode,
  aspect,
  minWidth,
  rounded,
  onSelectionPixelsChange,
}: FreeformCropStageProps): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [layout, setLayout] = useState<ImageLayout | null>(null);
  const [selection, setSelection] = useState<RectSelection | null>(null);
  const dragRef = useRef<
    | {
        type: 'move';
        startX: number;
        startY: number;
        origin: RectSelection;
      }
    | {
        type: 'resize';
        handle: CropHandle;
        startX: number;
        startY: number;
        origin: RectSelection;
      }
    | null
  >(null);

  const recomputeLayout = useCallback(async () => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const image = await loadHtmlImage(imageSrc);
    const stageW = container.clientWidth;
    const stageH = container.clientHeight;
    const normalizedRotation = ((rotation % 360) + 360) % 360;
    const swapped = normalizedRotation === 90 || normalizedRotation === 270;
    const mediaW = swapped ? image.naturalHeight : image.naturalWidth;
    const mediaH = swapped ? image.naturalWidth : image.naturalHeight;
    const scale = Math.min(stageW / mediaW, stageH / mediaH);
    const width = mediaW * scale;
    const height = mediaH * scale;
    const nextLayout: ImageLayout = {
      left: (stageW - width) / 2,
      top: (stageH - height) / 2,
      width,
      height,
      naturalWidth: image.naturalWidth,
      naturalHeight: image.naturalHeight,
      scale,
    };
    setLayout(nextLayout);
    setSelection((prev) => createInitialSelection(nextLayout, aspect, minWidth, prev));
  }, [aspect, imageSrc, minWidth, rotation]);

  useEffect(() => {
    void recomputeLayout();
  }, [recomputeLayout]);

  useEffect(() => {
    const onResize = () => {
      void recomputeLayout();
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [recomputeLayout]);

  useEffect(() => {
    if (!layout || !selection) {
      onSelectionPixelsChange(null);
      return;
    }
    onSelectionPixelsChange(mapSelectionToRotatedPixels({ selection, layout, rotation }));
  }, [layout, onSelectionPixelsChange, rotation, selection]);

  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || !layout) {
        return;
      }

      if (drag.type === 'move') {
        const dx = event.clientX - drag.startX;
        const dy = event.clientY - drag.startY;
        setSelection({
          x: clamp(drag.origin.x + dx, layout.left, layout.left + layout.width - drag.origin.width),
          y: clamp(drag.origin.y + dy, layout.top, layout.top + layout.height - drag.origin.height),
          width: drag.origin.width,
          height: drag.origin.height,
        });
        return;
      }

      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      setSelection(
        resizeRectSelection({
          origin: drag.origin,
          handle: drag.handle,
          dx,
          dy,
          layout,
          aspect,
          minWidth,
        }),
      );
    };

    const onPointerUp = () => {
      dragRef.current = null;
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [aspect, layout, minWidth]);

  return (
    <div ref={containerRef} className="absolute inset-0 overflow-hidden">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={imageSrc}
        alt=""
        draggable={false}
        className="pointer-events-none absolute max-w-none select-none"
        style={
          layout
            ? {
                left: layout.left + layout.width / 2,
                top: layout.top + layout.height / 2,
                width: layout.naturalWidth * layout.scale,
                height: layout.naturalHeight * layout.scale,
                transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
              }
            : { display: 'none' }
        }
      />

      {selection ? (
        <>
          <div
            className={cn(
              'absolute cursor-move border-2 border-white bg-transparent',
              rounded ? 'rounded-3xl' : 'rounded-sm',
            )}
            style={{
              left: selection.x,
              top: selection.y,
              width: selection.width,
              height: selection.height,
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.55)',
            }}
            onPointerDown={(event) => {
              event.preventDefault();
              dragRef.current = {
                type: 'move',
                startX: event.clientX,
                startY: event.clientY,
                origin: selection,
              };
            }}
          >
            {mode === 'banner' ? <BannerDeviceGuides /> : null}
          </div>

          <CropHandles
            box={selection}
            onResizeStart={(handle, event) => {
              event.preventDefault();
              event.stopPropagation();
              dragRef.current = {
                type: 'resize',
                handle,
                startX: event.clientX,
                startY: event.clientY,
                origin: selection,
              };
            }}
          />
        </>
      ) : null}
    </div>
  );
}
